const AUTH_TOKEN_KEY = 'authToken';
const USER_ID_KEY = 'userId';

const safeStorageGetItem = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSetItem = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // ignore
  }
};

const safeStorageRemoveItem = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
};

const getTokenFromAnyStorage = () =>
  safeStorageGetItem(localStorage, AUTH_TOKEN_KEY) ||
  safeStorageGetItem(sessionStorage, AUTH_TOKEN_KEY);

const getUserIdFromAnyStorage = () =>
  safeStorageGetItem(localStorage, USER_ID_KEY) ||
  safeStorageGetItem(sessionStorage, USER_ID_KEY);

const decodeTokenPayload = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  const payload = decodeTokenPayload(token);
  if (!payload || typeof payload.exp !== 'number') return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds;
};

export const clearAuthSession = () => {
  safeStorageRemoveItem(localStorage, AUTH_TOKEN_KEY);
  safeStorageRemoveItem(localStorage, USER_ID_KEY);
  safeStorageRemoveItem(sessionStorage, AUTH_TOKEN_KEY);
  safeStorageRemoveItem(sessionStorage, USER_ID_KEY);
};

export const setAuthSession = (token, userId) => {
  if (token) {
    // Persist auth for multi-day JWT expiry (e.g. 14 days).
    safeStorageSetItem(localStorage, AUTH_TOKEN_KEY, token);
    // Best-effort cleanup to avoid having two sources of truth.
    safeStorageRemoveItem(sessionStorage, AUTH_TOKEN_KEY);
  }
  if (userId !== undefined && userId !== null) {
    safeStorageSetItem(localStorage, USER_ID_KEY, String(userId));
    safeStorageRemoveItem(sessionStorage, USER_ID_KEY);
  }
};

export const getAuthToken = () => {
  const token = getTokenFromAnyStorage();
  if (!token) return null;
  if (isTokenExpired(token)) {
    clearAuthSession();
    return null;
  }
  // If token was only in sessionStorage (old behavior), migrate it to localStorage.
  const localToken = safeStorageGetItem(localStorage, AUTH_TOKEN_KEY);
  if (!localToken) {
    safeStorageSetItem(localStorage, AUTH_TOKEN_KEY, token);
    const uid = getUserIdFromAnyStorage();
    if (uid) safeStorageSetItem(localStorage, USER_ID_KEY, String(uid));
    safeStorageRemoveItem(sessionStorage, AUTH_TOKEN_KEY);
    safeStorageRemoveItem(sessionStorage, USER_ID_KEY);
  }
  return token;
};

export const getUserId = () => {
  const token = getAuthToken();
  if (!token) return null;
  return getUserIdFromAnyStorage();
};

export const isAuthenticated = () => Boolean(getAuthToken());
